import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui';
import { Loader2 } from 'lucide-react';
import type { IAdminMessageConfirmModalProps } from '@/interfaces';
import { MessageAudienceEnum } from '@/enums';
import { GENDER_LABELS, RELIGION_LABELS, countryName } from '../constants';

const audienceLabel = (audience: MessageAudienceEnum) => {
  if (audience === MessageAudienceEnum.ALL) return 'à tous les utilisateurs Heyama';
  if (audience === MessageAudienceEnum.ALL_IOS) return 'à tous les utilisateurs Heyama ayant un iPhone';
  if (audience === MessageAudienceEnum.ALL_ANDROID) return 'à tous les utilisateurs Heyama ayant un Android';
  return 'aux utilisateurs sélectionnés';
};

const ConfirmModal = ({
  open,
  audience,
  excludedUsers,
  genderFilter,
  ageRange,
  religionFilter,
  countryFilter,
  isSubmitting = false,
  onCancel,
  onConfirm
}: IAdminMessageConfirmModalProps) => {
  const isBroadcast = audience !== MessageAudienceEnum.SPECIFIC;
  const hasGenderFilter = isBroadcast && genderFilter.length > 0;
  const hasAgeFilter = isBroadcast && (ageRange.min != null || ageRange.max != null);
  const hasReligionFilter = isBroadcast && religionFilter.length > 0;
  const hasCountryFilter = isBroadcast && countryFilter.length > 0;
  const hasExcludedUsers = isBroadcast && excludedUsers.length > 0;

  const genderLabel = hasGenderFilter
    ? genderFilter.map((g) => GENDER_LABELS[g] ?? g).join(' + ')
    : null;

  const ageLabel = hasAgeFilter
    ? ageRange.min != null && ageRange.max != null
      ? `${ageRange.min}–${ageRange.max} ans`
      : ageRange.min != null
        ? `${ageRange.min} ans et plus`
        : `${ageRange.max} ans et moins`
    : null;

  const religionLabel = hasReligionFilter
    ? religionFilter.map((r) => RELIGION_LABELS[r] ?? r).join(', ')
    : null;

  const countryLabel = hasCountryFilter ? countryFilter.map(countryName).join(', ') : null;

  const excludedLabel = hasExcludedUsers
    ? excludedUsers.length === 1
      ? excludedUsers[0].name
      : `${excludedUsers.length} utilisateurs`
    : null;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen && !isSubmitting) onCancel(); }}>
      <DialogContent className='w-[calc(100%-2rem)] sm:max-w-[420px] rounded-2xl duration-0 animate-none'>
        <DialogHeader>
          <DialogTitle className='text-destructive'>Confirmer l’envoi</DialogTitle>
        </DialogHeader>

        <div className='py-2 space-y-3 text-sm text-foreground'>
          <p>Le message sera envoyé {audienceLabel(audience)}.</p>

          {(hasGenderFilter || hasAgeFilter || hasReligionFilter || hasCountryFilter) && (
            <div className='rounded-xl border border-border bg-muted/30 px-3 py-2 space-y-1 text-xs text-muted-foreground'>
              <p className='font-medium text-foreground'>Filtres actifs</p>
              {genderLabel && (
                <p>Genre : <span className='text-foreground font-medium'>{genderLabel}</span></p>
              )}
              {religionLabel && (
                <p>Religion : <span className='text-foreground font-medium'>{religionLabel}</span></p>
              )}
              {countryLabel && (
                <p>Pays : <span className='text-foreground font-medium'>{countryLabel}</span></p>
              )}
              {ageLabel && (
                <p>Âge : <span className='text-foreground font-medium'>{ageLabel}</span></p>
              )}
            </div>
          )}

          {excludedLabel && (
            <div className='rounded-xl border border-border bg-muted/30 px-3 py-2 space-y-1 text-xs text-muted-foreground'>
              <p className='font-medium text-foreground'>Exclusions</p>
              <p>Exclus : <span className='text-foreground font-medium'>{excludedLabel}</span></p>
            </div>
          )}

          <div className='rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600'>
            Cette action est irréversible.
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onCancel} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button variant='destructive' onClick={onConfirm} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Envoi...
              </>
            ) : (
              'Confirmer'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmModal;
